import React from "react";

/**
 * Props for the WheelSegment component
 */
interface WheelSegmentProps {
  /** Name displayed in the segment */
  name: string;
  /** Index for unique identification */
  index: number;
  /** Angle for the text positioning */
  angle: number;
  /** Radius of the wheel for text positioning */
  radius: number;
  /** Callback function to remove the segment */
  onRemove: () => void;
  /** Callback function to edit the segment name */
  onEdit: (newName: string) => void;
  /** Callback function for drag start event */
  onDragStart: (e: React.DragEvent, index: number) => void;
  /** Callback function for drag over event */
  onDragOver: (e: React.DragEvent) => void;
  /** Callback function for drop event */
  onDrop: (e: React.DragEvent, index: number) => void;
  /** Whether this name is currently off the wheel */
  isPicked?: boolean;
  /** Toggle whether this name is on the spinner */
  onToggleOnWheel?: () => void;
  /** Disable toggling while the wheel is spinning */
  isToggleDisabled?: boolean;
}

/**
 * Component that represents a draggable segment in a wheel interface.
 * Supports editing, removal, and drag-and-drop reordering.
 * Provides visual feedback for user interactions.
 *
 * @param props - The component props
 * @returns A list item representing a wheel segment
 */
const WheelSegment: React.FC<WheelSegmentProps> = ({
  name,
  index,
  onRemove,
  onEdit,
  onDragStart,
  onDragOver,
  onDrop,
  isPicked = false,
  onToggleOnWheel,
  isToggleDisabled = false,
}) => {
  return (
    <li
      className={`mb-2 flex items-center ${isPicked ? "opacity-50" : ""}`}
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e)}
      onDrop={(e) => onDrop(e, index)}
    >
      <label
        className="mr-2 flex shrink-0 items-center"
        title={isPicked ? "Put this name back on the wheel" : "Take this name off the wheel"}
      >
        <input
          type="checkbox"
          checked={!isPicked}
          disabled={isToggleDisabled}
          onChange={onToggleOnWheel}
          onMouseDown={(e) => e.stopPropagation()}
          className="size-4"
          aria-label={
            isPicked
              ? `Put ${name} back on the wheel`
              : `Take ${name} off the wheel`
          }
        />
      </label>
      <input
        type="text"
        value={name}
        onChange={(e) => onEdit(e.target.value)}
        className={`font-teacher mr-2 grow rounded border border-current ${"bg-white text-black"} p-2 text-base`}
        placeholder="Edit name"
        title={`Edit name ${index + 1}`}
      />
      {isPicked && (
        <span className="mr-2 whitespace-nowrap text-sm text-gray-500">
          Off wheel
        </span>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="rounded border border-black bg-red-500 px-2 py-1 text-white transition duration-300 hover:bg-red-600"
      >
        Remove
      </button>
    </li>
  );
};

export default WheelSegment;
